import DOMPurify, { type Config } from 'isomorphic-dompurify';

// sanitize as _sanitize,
// addHook,
import type { MultilanguageContent } from '../types/multilanguage';

import { isArray, isObject } from './assertFunctions';

// DOMPurify removes target attribute because it could introduce vulnerability
// It also reverses the attribute's order that it gets from browser
// This behaviour makes false positive for changes on FE since we make a simple string comparison
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if ('target' in node) {
    node.removeAttribute('target');
    node.setAttribute('target', '_blank');
    const rel = node.getAttribute('rel');
    node.removeAttribute('rel');
    node.setAttribute('rel', rel ?? 'noopener noreferrer');
  }
});

export type SanitizeConfig = Config & {
  RETURN_DOM_FRAGMENT?: false | undefined;
  RETURN_DOM?: false | undefined;
};

export function sanitize(text: string, config?: SanitizeConfig) {
  return DOMPurify.sanitize(text, config ?? {});
}

export function sanitizeExport(text: string, sanitizeConfig?: SanitizeConfig) {
  const replaceRegex = /&(nbsp|amp|quot|lt|gt);/g;
  const replaceChars = { nbsp: ' ', amp: '&', quot: '"', lt: '<', gt: '>' };

  return sanitize(
    text,
    sanitizeConfig ?? {
      ALLOWED_TAGS: [],
    },
  ).replace(replaceRegex, (_, char: keyof typeof replaceChars) => {
    return replaceChars[char];
  });
}

export function sanitizeMultilanguage<T extends MultilanguageContent<string>>(
  content: T,
  sanitizeConfig?: SanitizeConfig,
) {
  return Object.entries(content).reduce((acc, [key, value]) => {
    acc[key as keyof T] = sanitize(value, sanitizeConfig) as T[keyof T];
    return acc;
  }, {} as T);
}

/** For use in class validators
 * - For strings return sanitized value.
 * - For objects string properties will be sanitized, others will be left as-is.
 * - For other values return directly and leave the rejection to the associated validator. */
export function sanitizeMultilanguageTransform(
  content: unknown,
  sanitizeConfig?: SanitizeConfig,
) {
  if (typeof content === 'string') return sanitize(content, sanitizeConfig);
  if (!isObject(content)) {
    return content;
  }
  return Object.entries(content).reduce<Record<string, unknown>>(
    (acc, [key, value]) => {
      acc[key] =
        typeof value === 'string' ? sanitize(value, sanitizeConfig) : value;
      return acc;
    },
    {},
  );
}

/** For use in class validators.
 * - For strings return sanitized value.
 * - For objects string properties will be sanitized, others will be left as-is.
 * - Arrays will have each element validated.
 * - Object like properties of objects will be traversed.
 * - For other values return directly and leave rejection to the associated validator. */
export function sanitizeMultilanguageTransformNested<T>(
  content: T,
  sanitizeConfig?: SanitizeConfig,
): T {
  const sanitizeContent = <T>(value: T, sanitizeConfig?: SanitizeConfig): T => {
    if (typeof content === 'string')
      return sanitize(content, sanitizeConfig) as T;

    if (isArray(value)) {
      return value.map((val) => sanitizeContent(val, sanitizeConfig)) as T;
    }

    if (isObject(value)) {
      return Object.entries(value).reduce(
        (acc, [key, value]) => {
          if (isArray(value)) {
            acc[key] = value.map((v) => sanitizeContent(v, sanitizeConfig));
            return acc;
          }
          if (typeof value === 'string') {
            acc[key] = sanitize(value, sanitizeConfig);
            return acc;
          } else if (isObject(value)) {
            acc[key] = sanitizeContent(value, sanitizeConfig);
          } else {
            acc[key] = value;
          }

          return acc;
        },
        // eslint-disable-next-line @typescript-eslint/prefer-reduce-type-parameter
        {} as Record<string, unknown>,
      ) as T;
    }
    return value;
  };
  return sanitizeContent(content, sanitizeConfig);
}
